// Contents of this file was produced with a help of a code generation tool and
// subsequently reviewed and edited by a human.
//
// While some of the code was created by an AI assisted service, manual adjustments
// have been made to ensure correctness, readability, functionality and compliance
// to Avalara's coding standards and guidelines.
// Any future modifications should be approached with caution to avoid
// overwriting the manual changes.
//
//
// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.
//
// For more information, refer to the documentation or contact the development team.

const SESSION_KEY = 'cartv2_session_data';
const ACCOUNT_SESSION_KEY = 'cartv2_account_data';
const SOURCE_ID_KEY = 'cartv2_source_id';
const SOURCE_ID_VALIDATION_KEY = 'cartv2_source_validation';
const FINAL_CART_KEY = 'cartv2_final_cart';

export interface CartSessionData {
  partnerName: string;
  partnerId: number;
  partnerImage: string;
  partnerImageExists: boolean;
  productPricing: any[];
  propertyCount: number;
  subscriptionFeeTotal: number;
  licenseFeeTotal: number;
  total: number;
  subscriptionLabel: string;
  selectedProductId?: number;
}

export interface BillingAddress {
  AddressLine1: string;
  AddressLine2: string | null;
  AddressLine3: string | null;
  City: string;
  State: string;
  PostalCode: string;
}

export interface Customer {
  CustomerId: string;
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber: string;
  BillingAddress: BillingAddress;
}

export interface PropertyAddress {
  AddressLine1: string;
  AddressLine2: string | null;
  AddressLine3: string | null;
  City: string;
  State: string;
  PostalCode: string;
}

export interface Location {
  PropertyId: string;
  LocationId: string;
  Nickname: string;
  Address: PropertyAddress;
  LicenseInfo: any;
  PersonOwners: any;
}

export interface AccountData {
  PartnerId: number;
  PartnerName: string;
  UserId?: string;
  TransactionId?: string;
  Customer: Customer;
  Locations: Location[];
  RentalProperties: Location[];
  ReceivedDate: string;
  [key: string]: any; // Allow for additional properties
}

export interface CartModel {
  partnerId: string | number;
  partnerName: string;
  partnerImage: string;
  partnerImageExists: boolean;
  propertyCount?: number;
  subscriptionFeeTotal?: number;
  licenseFeeTotal?: number;
  total?: number;
  subscriptionLabel?: string;
  productPricing?: any[];
}

export const getCartSessionData = (): CartSessionData | null => {
  try {
    const sessionData = sessionStorage.getItem(SESSION_KEY);
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    console.error('Error reading cart session data:', error);
    return null;
  }
};

export const setCartSessionData = (data: CartSessionData): void => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error writing cart session data:', error);
  }
};

/**
 * Convert CartSessionData to CartModel format
 */
export const convertSessionDataToCartModel = (sessionData: CartSessionData): CartModel => {
  return {
    partnerId: sessionData.partnerId,
    partnerName: sessionData.partnerName,
    partnerImage: sessionData.partnerImage,
    partnerImageExists: sessionData.partnerImageExists,
    propertyCount: sessionData.propertyCount,
    subscriptionFeeTotal: sessionData.subscriptionFeeTotal,
    licenseFeeTotal: sessionData.licenseFeeTotal,
    total: sessionData.total,
    subscriptionLabel: sessionData.subscriptionLabel,
    productPricing: sessionData.productPricing
  };
};

/**
 * Create default fallback data when no session data is available
 */
export const createDefaultFallbackData = (partnerId: string): CartSessionData => {
  return {
    partnerId: parseInt(partnerId) || 0,
    partnerName: 'unknown',
    partnerImage: 'Not Found',
    partnerImageExists: false,
    propertyCount: 1,
    productPricing: [],
    subscriptionFeeTotal: 0,
    licenseFeeTotal: 0,
    total: 0,
    subscriptionLabel: 'Subscription'
  };
};

/**
 * Snapshot of the final cart totals captured when the user submits the payment
 * page. These values are already multiplied by the selected property count, so
 * the success page can report the exact purchased cart to the Adobe data layer
 * without re-deriving pricing.
 */
export interface FinalCart {
  propertyCount: number;
  subscriptionFeeTotal: number;
  licenseFeeTotal: number;
  total: number;
}

export const setFinalCart = (cart: FinalCart): void => {
  try {
    sessionStorage.setItem(FINAL_CART_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error setting final cart in session storage:', error);
  }
};

export const getFinalCart = (): FinalCart | null => {
  try {
    const data = sessionStorage.getItem(FINAL_CART_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting final cart from session storage:', error);
    return null;
  }
};

// Account Data Utility Functions
export const setAccountData = (accountData: AccountData): void => {
  try {
    sessionStorage.setItem(ACCOUNT_SESSION_KEY, JSON.stringify(accountData));
  } catch (error) {
    console.error('Error setting account data in session storage:', error);
  }
};

export const getAccountData = (): AccountData | null => {
  try {
    const data = sessionStorage.getItem(ACCOUNT_SESSION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting account data from session storage:', error);
    return null;
  }
};


// SourceId Storage Interface
export interface SourceIdValidation {
  sourceId: string;
  isValid: boolean;
  partnerId?: number;
  partnerSourceId?: string;
  validatedAt: string;
}

// SourceId Utility Functions
export const setSourceId = (sourceId: string): void => {
  try {
    sessionStorage.setItem(SOURCE_ID_KEY, sourceId);
  } catch (error) {
    console.error('Error setting sourceId in session storage:', error);
  }
};

export const getSourceId = (): string | null => {
  try {
    return sessionStorage.getItem(SOURCE_ID_KEY);
  } catch (error) {
    console.error('Error getting sourceId from session storage:', error);
    return null;
  }
};

export const setSourceIdValidation = (validation: SourceIdValidation): void => {
  try {
    sessionStorage.setItem(SOURCE_ID_VALIDATION_KEY, JSON.stringify(validation));
  } catch (error) {
    console.error('Error setting sourceId validation in session storage:', error);
  }
};

export const getSourceIdValidation = (): SourceIdValidation | null => {
  try {
    const data = sessionStorage.getItem(SOURCE_ID_VALIDATION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting sourceId validation from session storage:', error);
    return null;
  }
};
